import { styled } from '@mui/material/styles';
import { Box, Paper, Typography } from '@mui/material';

// ─── Page shell ───────────────────────────────────────────────────────────────

export const PageWrapperStyled = styled('div', {
  name: 'CI_PageWrapper',
})(({ theme }) => ({
  padding: theme.spacing(2, 0, 4),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
}));

// ─── Compact search bar ───────────────────────────────────────────────────────

export const SearchBarStyled = styled(Paper, {
  name: 'CI_SearchBar',
})(({ theme }) => ({
  display: 'inline-flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.25, 2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  boxShadow: 'none',
  alignSelf: 'flex-start',
}));

// ─── Results grid — always visible ───────────────────────────────────────────

export const ResultsGridStyled = styled('div', {
  name: 'CI_ResultsGrid',
})(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: theme.spacing(2.5),
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

// ─── Info card ────────────────────────────────────────────────────────────────

export const InfoCardStyled = styled(Paper, {
  name: 'CI_InfoCard',
})(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  boxShadow: 'none',
}));

// bandcolor prop intentionally passed to Box (renders as div — no DOM issue)
export const CardHeaderBandStyled = styled(Box, {
  name: 'CI_CardHeaderBand',
})<{ bandcolor: string }>(({ bandcolor }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '10px 16px',
  backgroundColor: `${bandcolor}12`,
  borderBottom: `1px solid ${bandcolor}28`,
}));

export const CardIconCircleStyled = styled(Box, {
  name: 'CI_CardIconCircle',
})<{ bandcolor: string }>(({ bandcolor }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 36,
  height: 36,
  borderRadius: '50%',
  backgroundColor: `${bandcolor}20`,
  color: bandcolor,
  fontSize: '0.9rem',
  flexShrink: 0,
}));

export const CardBodyStyled = styled(Box, {
  name: 'CI_CardBody',
})(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  // padding only for non-field states; field rows handle their own padding
  padding: theme.spacing(0),
}));

// ─── Field list (data state) ─────────────────────────────────────────────────

export const FieldListStyled = styled('div', {
  name: 'CI_FieldList',
})(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
}));

export const FieldRowStyled = styled(Box, {
  name: 'CI_FieldRow',
})(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: theme.spacing(1.25, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderBottom: 'none',
  },
}));

export const FieldValueStyled = styled(Typography, {
  name: 'CI_FieldValue',
})(({ theme }) => ({
  fontSize: '0.9rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  wordBreak: 'break-word',
  lineHeight: 1.4,
}));

// ─── Placeholder state ────────────────────────────────────────────────────────

export const PlaceholderStateStyled = styled(Box, {
  name: 'CI_PlaceholderState',
})(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1.25),
  padding: theme.spacing(4, 3),
  color: theme.palette.text.disabled,
  textAlign: 'center',
  fontSize: '0.85rem',
}));

// ─── Not-found state ─────────────────────────────────────────────────────────

export const NotFoundStateStyled = styled(Box, {
  name: 'CI_NotFoundState',
})(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1.25),
  padding: theme.spacing(4, 3),
  color: theme.palette.error.main,
  textAlign: 'center',
  fontSize: '0.85rem',
}));

// ─── Loading skeleton block ───────────────────────────────────────────────────

export const SkeletonBlockStyled = styled(Box, {
  name: 'CI_SkeletonBlock',
})(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2),
  gap: theme.spacing(0.5),
}));

// ─── Column pair (Employer + Owner stacked) ───────────────────────────────────

export const ColumnPairStyled = styled('div', {
  name: 'CI_ColumnPair',
})(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
}));
